<script>
import GoogleMapsMixin from "@/mixins/google-maps.mixin.ts";
import LocationIconLink from "@/components/LocationIconLink.vue";
import { mapState } from "pinia";
import { useAppStore } from "@/store/app.store.ts";
import CurrentLocationDot from "@/components/status/CurrentLocationDot.vue";
import { DAY_IN_SECONDS, HOUR_IN_SECONDS, TWENTY_MINUTES_IN_SECONDS } from "@/data/time.ts";
import { useDateTime } from "@/composables/index.ts";
import AlertMessage from "@/components/AlertMessage.vue";
import { AlertMessageType } from "@/data/components.ts";
import dayjs from "dayjs";

const { dateTZ, timeAgo } = useDateTime();

export default {
  name: "TruckLocationInfo",
  components: { AlertMessage, CurrentLocationDot, LocationIconLink },
  mixins: [GoogleMapsMixin],
  props: {
    location: Object,
    time: String,
    showChangedBy: {
      type: Boolean,
      default: false,
    },
    showIndicator: {
      type: Boolean,
      default: false,
    },
    showTooltip: {
      type: Boolean,
      default: true,
    },
    hideDot: {
      type: Boolean,
      default: false,
    },
    showInfoMenu: {
      type: Boolean,
      default: false,
    },
    currentLocation: {
      type: Object,
      default: null,
    },
    currentLocationTime: {
      type: [String, null],
      default: null,
    },
    showAlert: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(useAppStore, ["dispatcher"]),
    copyLocation() {
      if (!this.location) return "";
      if (this.location.zipCode) return this.location.zipCode;
      return this.location.city + ", " + this.location.state;
    },
    fullAddress() {
      return this.location.fullAddress;
    },
    showChangedByTooltip() {
      return this.showChangedBy && this.location.changedBy;
    },
    dotSettings() {
      return [
        {
          seconds: DAY_IN_SECONDS,
          color: "grey",
        },
        {
          seconds: HOUR_IN_SECONDS,
          color: "orange",
        },
        {
          seconds: TWENTY_MINUTES_IN_SECONDS,
          color: "yellow",
        },
      ];
    },
    changedAt() {
      return dateTZ(this.location.changedAt, this.dispatcher.timezone);
    },
    timePassed() {
      return timeAgo(this.time);
    },
    timePassedInMinutes() {
      return dayjs().diff(dayjs(this.time), "minutes");
    },
    is15MinutesExceeded() {
      return this.timePassedInMinutes > 15;
    },
    alertType() {
      return this.timePassedInMinutes > 30 ? AlertMessageType.danger : AlertMessageType.warning;
    },
  },
};
</script>

<template>
  <div>
    <div class="d-flex justify-space-between">
      <div>
        <v-menu v-if="showChangedByTooltip" :open-on-hover="true" location="top start" offset="4">
          <template #activator="{ props }">
            <div v-if="location.city" class="d-flex align-center">
              <span v-clipboard.uncolored="copyLocation" class="location" v-bind="props">
                {{ fullAddress }}
              </span>
              <location-icon-link :coordinates="location.coordinates"></location-icon-link>
            </div>
          </template>
          <v-card>
            <v-card-text>
              <div>
                Changed at:
                <span class="font-weight-500 text-primary">
                  {{ changedAt }}
                </span>
              </div>
              <div>
                Changed by: <span class="font-weight-500 text-primary">{{ location.changedBy }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-menu>
        <div v-if="!showChangedByTooltip && location?.city" class="d-flex align-center">
          <span v-clipboard.uncolored="copyLocation" class="location">
            {{ fullAddress }}
          </span>
          <location-icon-link :coordinates="location.coordinates"></location-icon-link>
        </div>

        <current-location-dot
          v-if="time"
          :dot-class="`font-weight-600 text-grey-darken-2 mb-1 font-size-11 line-height-1 mt-2 ${!hideDot ? 'ml-10px' : ''}`"
          :dot-settings="dotSettings"
          :hide-dot="hideDot"
          :time="time"
          :timezone="location.timezone"
          show-tooltip
        />
      </div>

      <v-menu v-if="showInfoMenu && currentLocation" min-width="200">
        <template #activator="{ props: menuProps }">
          <v-icon color="primary" size="small" v-bind="menuProps">mdi-crosshairs-gps</v-icon>
        </template>

        <v-card>
          <v-card-text>
            <div class="font-size-16 font-weight-500 text-grey-darken-3 mb-3">Current location</div>

            <span>{{ currentLocation.fullAddress }}</span>
            <current-location-dot
              v-if="currentLocationTime"
              :dot-class="`font-weight-600 text-grey-darken-2 mb-1 font-size-11 line-height-1 mt-2 ${!hideDot ? 'ml-10px' : ''}`"
              :dot-settings="dotSettings"
              :hide-dot="hideDot"
              :time="currentLocationTime"
              :timezone="currentLocation.timezone"
              show-tooltip
            />
          </v-card-text>
        </v-card>
      </v-menu>
    </div>

    <alert-message v-if="showAlert && is15MinutesExceeded" :type="alertType" class="w-100 mt-2">{{
      $t("general.location-was-updated", { minutes: timePassed })
    }}</alert-message>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.location {
  line-height: 17px;

  &:hover {
    color: $primary;
  }
}
</style>
